import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, Observable, timer } from 'rxjs';
import { finalize, map, takeWhile } from 'rxjs/operators';

import {
  TWO_FACTOR_USE_CASES,
  TwoFactorUseCases
} from '@app/domain/use-cases/ptrab/two-factor/two-factor.use-cases.contract';
import {
  TwoFactorError,
  TwoFactorOperationCode,
  TwoFactorValidationResult,
  TwoFactorValidationStatus
} from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { Buttons } from '@app/entities/models/buttons/buttons';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { ContentModalComponent } from '@app/presentation/components/modals/content-modal/content-modal.component';
import { Logger, ModalManager, AlertService } from '@app/services';
import { LoadingService } from '@app/services/loading/loading.service';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { MButtonComponent } from '@mercadona/components-musi/button';
import { M_DIALOG_DATA } from '@mercadona/components-musi/dialog';
import { MInputComponent } from '@mercadona/components-musi/input';
import { MTranslatePipe, MTranslateService } from '@mercadona/core/translate';

import { MessageErrorComponent } from '@/components/messages';

@Component({
  selector: 'app-ptrab-code-validation',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ContentModalComponent,
    MTranslatePipe,
    MessageErrorComponent,
    MButtonComponent,
    MInputComponent
  ],
  templateUrl: './code-validation.component.html',
  styleUrls: ['./code-validation.component.scss']
})
export class CodeValidationComponent implements OnInit {
  phoneId!: string;
  operation!: TwoFactorOperationCode;

  buttons: Buttons[] = [
    {
      text: 'COMMON.CONFIRM',
      enabled: false,
      onClick: () => this.validateCode()
    }
  ];

  form!: FormGroup;
  maxTriesExceeded = false;
  expiredCode = false;
  timeRemaining$!: Observable<number>;
  sendAgainAvailable!: boolean;
  waitingTime!: number;

  readonly codeLength = 6;
  readonly defaultWaitingTime = 300;
  private logger = new Logger('CodeValidationFormComponent');
  #twoFactorservice: TwoFactorUseCases = inject(TWO_FACTOR_USE_CASES);

  constructor(
    @Inject(M_DIALOG_DATA) public data: MSafeAny,
    private formBuilder: FormBuilder,
    private loadingService: LoadingService,
    private alertService: AlertService,
    private translate: MTranslateService,
    private modalManager: ModalManager
  ) {}

  ngOnInit() {
    this.phoneId = this.data.phoneId;
    this.operation = this.data.operation;

    this.setupForm();
    this.sendCode();
  }

  validateCode() {
    this.loadingService.show();
    this.#twoFactorservice
      .validateCode(this.operation, this.form.get('code')?.value, this.phoneId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (hash) => {
          const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.SUCCESS, hash };
          this.modalManager.dismissMatModal(result);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === ErrorCodes.BAD_REQUEST && error.error.code) {
            this.handleTwoFactorError(error.error.code);
            return;
          }

          this.handleGenericError(error);
        }
      });
  }

  async sendCode() {
    try {
      await this.sendValidationCode();
    } catch (error) {
      this.handleGenericError(error);
      const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.ERROR };
      this.modalManager.dismissMatModal(result);
    }
  }

  async resendCode() {
    try {
      await this.sendValidationCode();
      this.alertService.showSuccess(
        this.translate.translate('MODIFY_ADDRESS.CODE_SENT.TITLE'),
        this.translate.translate('MODIFY_ADDRESS.CODE_SENT.SUBTITLE')
      );
    } catch (error) {
      this.handleGenericError(error);
    } finally {
      this.form.reset();
      this.form.controls.code.setErrors(null);
      this.expiredCode = false;
    }
  }

  showCodeValidationForm() {
    return this.form && !this.maxTriesExceeded && !this.expiredCode;
  }

  private async handleTwoFactorError(error: TwoFactorError) {
    switch (error) {
      case TwoFactorError.VALIDATION_CODE_INVALID:
        this.form.controls.code.setErrors({ invalidCode: true });
        break;
      case TwoFactorError.VALIDATION_CODE_MAX_TRIES_EXCEEDED:
        this.setMaxTriesError();
        break;
      case TwoFactorError.VALIDATION_CODE_EXPIRED:
        this.expiredCode = true;
        this.buttons[0].enabled = false;
        break;
      default:
        this.handleGenericError(error);
    }
  }

  private handleGenericError(error: MSafeAny) {
    this.alertService.showError(
      this.translate.translate('UNEXPECTED.ERROR.SUBTITLE'),
      this.translate.translate('UNEXPECTED.ERROR.TITLE')
    );
    this.logger.error(error);
  }

  private setMaxTriesError() {
    this.maxTriesExceeded = true;
    this.buttons = [
      {
        text: 'COMMON.RETURN',
        enabled: true,
        onClick: () => this.dismissBlockedUser()
      }
    ];
  }

  private dismissBlockedUser() {
    const result: TwoFactorValidationResult = { status: TwoFactorValidationStatus.USER_BLOCKED };
    this.modalManager.dismissMatModal(result);
  }

  private async sendValidationCode() {
    this.loadingService.show();
    try {
      const twoFactorResponse = await firstValueFrom(
        this.#twoFactorservice.sendValidationCodeToPhone(this.operation, this.phoneId)
      );
      this.waitingTime = twoFactorResponse?.blockingTime || this.defaultWaitingTime;
      this.startTimer();
    } finally {
      this.loadingService.hide();
    }
  }

  private startTimer() {
    this.sendAgainAvailable = false;
    this.timeRemaining$ = timer(0, 1000).pipe(
      map((n) => (this.waitingTime - n) * 1000),
      takeWhile((n) => n > 0),
      finalize(() => (this.sendAgainAvailable = true))
    );
  }

  private setupForm() {
    this.form = this.formBuilder.group({
      code: [
        '',
        Validators.compose([
          Validators.minLength(this.codeLength),
          Validators.maxLength(this.codeLength),
          Validators.required
        ])
      ]
    });

    this.form.valueChanges.subscribe(() => {
      this.buttons[0].enabled = this.form.value.code?.length === this.codeLength;
    });
  }
}
